import { HTMLAttributes, SVGAttributes } from 'react'
import Image from 'next/image'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import { default as BaseGrid } from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { useGridMotion } from './motion'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

/**
 * card-border
 */
export type CardBorderProps = SVGAttributes<SVGSVGElement> & { size: number }

export const CardBorder = ({ size, ...props }: CardBorderProps) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <defs>
        <linearGradient id="gradient" x1="0%" y1="0%" x2="50%" y2="50%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <mask id="hole-mask">
          <rect x="0" y="0" width={size} height={size} rx="8" ry="8" fill="white" />
          <rect x="1" y="1" width={size - 1} height={size - 1} rx="7" ry="7" fill="black" />
        </mask>
      </defs>
      <rect
        x="0"
        y="0"
        width={size}
        height={size}
        rx="8"
        ry="8"
        fill="url(#gradient)"
        mask="url(#hole-mask)"
      />
    </svg>
  )
}

/**
 * card
 */
export type CardModel = {
  title: string
  text: string
  link: string
  image: WPImage
}

export type CardProps = HTMLAttributes<HTMLDivElement> & CardModel

export const Card = ({ title, text, link, image, className, ...props }: CardProps) => {
  const { strings } = useStore()

  return (
    <div className={cn('grid h-full', className)} {...props}>
      <div className="pointer-events-none relative z-10 col-[1/-1] row-[1/-1] flex items-start">
        <CardBorder size={320} className="h-auto w-full" />
      </div>
      <div className="pointer-events-none relative z-10 col-[1/-1] row-[1/-1] flex items-end">
        <CardBorder size={320} className="h-auto w-full rotate-180" />
      </div>

      <div
        className={cn(
          'relative col-[1/-1] row-[1/-1]',
          'rounded-[0.7rem] bg-white-light/40 backdrop-blur-sm dark:bg-white-dark/40'
        )}
      >
        <div className="flex h-full w-full flex-col p-spacing-sm">
          <div className={cn('grid gap-y-spacing-sm pb-spacing-lg')}>
            {link ? (
              <Link href={link} scroll={false}>
                <Image
                  src={image.url}
                  alt={image.alt || title || ''}
                  width={image.width}
                  height={image.height}
                  className="aspect-[1.5] w-full rounded-radius-minimal object-cover"
                />
              </Link>
            ) : null}

            {title ? (
              <div className="flex">
                <Typography variant="large" className="font-medium">
                  {title}
                </Typography>
              </div>
            ) : null}

            {text ? (
              <div className="flex">
                <Typography variant="body">{text}</Typography>
              </div>
            ) : null}
          </div>

          {link ? (
            <div className="mt-auto flex">
              <Button variant="primary" asChild className="w-full">
                <Link href={link} scroll={false}>
                  <Typography mask>{strings?.labels?.read_more}</Typography>
                </Link>
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * grid
 */
export type GridProps = HTMLAttributes<HTMLDivElement> & {
  items: CardModel[]
}

const Grid = ({ items, ...props }: GridProps) => {
  const { motionRef } = useGridMotion()

  return (
    <Section ref={motionRef} {...props}>
      <Container>
        <BaseGrid className="gap-y-spacing-sm md:gap-y-gutter">
          {items.map((card, index) => (
            <Card
              key={index}
              className="col-span-2 md:col-span-3 lg:col-span-4"
              data-motion-card
              {...card}
            />
          ))}
        </BaseGrid>
      </Container>
    </Section>
  )
}

export default Grid
